import Vue from 'vue'
import VueI18n from 'vue-i18n'

import { LANG_FR, LANG_EN } from '@/consts/langs'
import { CONTENT_LANGUAGE_FRENCH, CONTENT_LANGUAGE_ENGLISH, USER_LANGUAGE_ENGLISH, USER_LANGUAGE_FRENCH } from '@/consts/enums'
import { VUE_APP_I18N_FALLBACK_LOCALE } from '@/helpers/env'
import MessageFormatFormatter from '@/helpers/message-format-formatter'

import dayjs from 'dayjs'
import './dayjs.fr'
import 'dayjs/locale/en'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import advancedFormat from 'dayjs/plugin/advancedFormat'

import merge from 'lodash/merge'

Vue.use(VueI18n)

const LOCALSTORAGE_LOCALE = 'app-locale'

const supportedLocales = [LANG_FR, LANG_EN]
let initialLocale = localStorage.getItem(LOCALSTORAGE_LOCALE)
if (!supportedLocales.includes(initialLocale)) initialLocale = supportedLocales[0]

dayjs.extend(localizedFormat)
dayjs.extend(advancedFormat)

const formatter = new MessageFormatFormatter({ locale: initialLocale })
const instanceI18n = new VueI18n({
  locale: initialLocale,
  fallbackLocale: VUE_APP_I18N_FALLBACK_LOCALE || LANG_FR,
  messages: loadLocaleMessages(),
  formatter: formatter
})

setLang(initialLocale)

function loadLocaleMessages() {
  const context = require.context('@', true, /\.i18n\.json$/i)
  const i18nMessages = []
  context.keys().forEach(k => i18nMessages.push(context(k)))
  return merge(...i18nMessages)
}

function getNextSupportedLocale(locale) {
  let idx = supportedLocales.indexOf(locale)
  idx += 1

  if (idx >= supportedLocales.length) idx = 0

  return supportedLocales[idx]
}

function setLang(lang) {
  localStorage.setItem(LOCALSTORAGE_LOCALE, lang)
  dayjs.locale(lang)
  instanceI18n.locale = lang

  document.documentElement.setAttribute('lang', lang)
}

function contentLanguageToUserLanguage(contentLanguage) {
  switch (contentLanguage) {
    case CONTENT_LANGUAGE_ENGLISH:
      return USER_LANGUAGE_ENGLISH
    case CONTENT_LANGUAGE_FRENCH:
    default:
      return USER_LANGUAGE_FRENCH
  }
}

function userLanguageToLang(userLanguage) {
  switch (userLanguage) {
    case USER_LANGUAGE_ENGLISH:
      return LANG_EN
    case USER_LANGUAGE_FRENCH:
    default:
      return LANG_FR
  }
}

const i18n = {
  instance: function() {
    return instanceI18n
  },
  locale: () => {
    return instanceI18n.locale
  },
  contentLanguage() {
    if (instanceI18n.locale === LANG_EN) {
      return CONTENT_LANGUAGE_ENGLISH
    } else {
      return CONTENT_LANGUAGE_FRENCH
    }
  },
  setLang,
  changeLang: () => {
    let lang = getNextSupportedLocale(instanceI18n.locale)
    i18n.setLang(lang)
  },
  dayjs: date => {
    return dayjs(date)
  },
  isLangValid: lang => supportedLocales.includes(lang),
  t() {
    return instanceI18n.t(...arguments)
  },
  userLanguage() {
    return contentLanguageToUserLanguage(this.contentLanguage())
  },
  setContentLanguageToUserLanguage(userLanguage) {
    let lang = userLanguageToLang(userLanguage)
    i18n.setLang(lang)
  }
}

export default i18n
